import React, { useRef, useState, useEffect, useCallback } from "react";
import * as THREE from "three";
import { useThree } from "@react-three/fiber";
import DraggableObject from "./DraggableObject";
import { RectStairsType, SquareSymbolType, zIndexEpsilon } from "../../types/floorplannerTypes";
import { SelectableSymbol, BoundingBox } from "./SelectableSymbol";
import { projectToWorld } from "./projectToWorld";
import { FloorplannerStoreContext } from "../../store/floorplannerStore";
import { Line } from "@react-three/drei";
import { editorStore } from "../../store/editorStore";
import { observer } from "mobx-react-lite";
import { set } from "mobx";
import { is } from '@react-three/fiber/dist/declarations/src/core/utils';
import PatternFillMesh from "./PatternFillMesh";
import ShapeProjectionOverlay from "./ShapeProjectionOverlay";
import { shouldShowShapePrimitiveProjection } from "../../features/floorplan3d/symbols/primitives/shapes/shapePrimitiveGeometry";
import { canvasAccentColor } from "../../constants/brandColors";
import { resolveFillOpacity } from "../../utils/fillOpacity";
import { getOpacityRemountEpoch } from "../../utils/opacityRemount";
import { isSymbolAspectRatioLocked } from "../../utils/symbolAspectRatio";

interface SquareSymbolProps {
  square: SquareSymbolType;
  onDragStart: (square: SquareSymbolType, offset: [number, number]) => void;
  onDrag: (newPosition: [number, number]) => void;
  onDragEnd: (endPosition: [number, number]) => void;
}

// Add validation function at the top
const validatePoints = (points: [number, number, number][]): [number, number, number][] => {
  return points.map(point => point.map(coord =>
    Number.isFinite(coord) ? coord : 0
  ) as [number, number, number]);
};

const SquareSymbol: React.FC<SquareSymbolProps> = observer(({
  square,
  onDragStart,
  onDragEnd,
  onDrag,
}) => {
  const { gl, camera } = useThree();
  const groupRef = useRef<THREE.Group>(null);
  const floorplannerStore = React.useContext(FloorplannerStoreContext);

  const lineWeight = square.lineWeight || floorplannerStore.symbolLineWeight;
  const [squareWidth, setSquareWidth] = useState(Number.isFinite(square.squareWidth) ? square.squareWidth : 1);
  const [squareHeight, setSquareHeight] = useState(Number.isFinite(square.squareHeight) ? square.squareHeight : 1);
  const [localPosition, setLocalPosition] = useState<[number, number]>([square.position.x, square.position.y]);

  const flipX = square.flipHorizontal ? -1 : 1; // Flip horizontally
  const flipY = square.flipVertical ? -1 : 1; // Flip vertically
  // Center and pivot are at origin since bounding box is centered
  const centerPosition: [number, number] = [0, 0];
  const lineColor = square.lineColor || floorplannerStore.lineColor;
  const pivot: [number, number] = [0, 0];
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const noopRaycast = () => null;
  const projectionActive = shouldShowShapePrimitiveProjection(square);
  const fillOpacity = resolveFillOpacity(square);
  const fillIsTransparent = fillOpacity < 1;

  // Calculate the bounding box of the square - returns corners relative to center (0,0)
  const calculateBoundingBox = useCallback((): BoundingBox => {
    if (!groupRef.current) {
      return {
        topLeft: [0, 0],
        topRight: [0, 0],
        bottomLeft: [0, 0],
        bottomRight: [0, 0],
        width: 0,
        height: 0,
        depth: 0,
      };
    }

    // Clone the object
    const tempGroup = groupRef.current.clone();

    // Save the original rotation
    const originalRotation = tempGroup.rotation.clone();

    // Temporarily reset the rotation to 0
    tempGroup.rotation.set(0, 0, 0);

    // Calculate the bounding box as if the object had no rotation
    const boundingBox = new THREE.Box3().setFromObject(tempGroup);
    const size = new THREE.Vector3();
    boundingBox.getSize(size);

    // Restore the original rotation
    tempGroup.rotation.copy(originalRotation);

    // Add line weight adjustment
    const lineWeightAdjustment = lineWeight / 100;
    const width = size.x + lineWeightAdjustment;
    const height = size.y + lineWeightAdjustment;
    const halfWidth = width / 2;
    const halfHeight = height / 2;

    // Return corners relative to center (0, 0) - consistent with how SvgSymbol works
    return {
      topLeft: [-halfWidth, halfHeight],
      topRight: [halfWidth, halfHeight],
      bottomLeft: [-halfWidth, -halfHeight],
      bottomRight: [halfWidth, -halfHeight],
      width: width,
      height: height,
      depth: 0,
    };
  }, [square.position.x, square.position.y, square.flipHorizontal, square.flipVertical, squareWidth, squareHeight, lineWeight]);

  const dragOffset = useRef({ x: 0, y: 0 });

  const onHandleDrag = (event: PointerEvent) => {
    const [worldX, worldY] = projectToWorld(event.clientX, event.clientY, gl, camera);
    const dy = worldY - square.position.y - dragOffset.current.y;
    // Position is handled by the store, no longer need to update centerPosition
  };

  const onPointerUpHandle = () => {
    gl.domElement.removeEventListener("pointermove", onHandleDrag);
    gl.domElement.removeEventListener("pointerup", onPointerUpHandle);
    floorplannerStore.setBlockDirty(false);
    floorplannerStore.setDirty();
  };

  const handleDrag = (newPosition: [number, number]) => {
    onDrag(newPosition);
  };
  // Call the function and destructure the returned values
  //const { rectPoints, extraLines } = generateRectPointsAndLines();

  // Handle Drag Right (Green Line)
  const dragOffsetRight = useRef({ x: 0, y: 0 });

  const onHandleDragRight = (event: PointerEvent) => {
    const [worldX] = projectToWorld(event.clientX, event.clientY, gl, camera);
    const dx = worldX - square.position.x - dragOffsetRight.current.x;
    floorplannerStore.updateSymbolProperty(square.id, "squareWidth", Math.max(0.1, squareWidth + dx));
  };

  const onPointerUpHandleRight = () => {
    gl.domElement.removeEventListener("pointermove", onHandleDragRight);
    gl.domElement.removeEventListener("pointerup", onPointerUpHandleRight);
    floorplannerStore.setBlockDirty(false);
    floorplannerStore.updateSymbolProperty(square.id, "squareWidth", squareWidth);
    floorplannerStore.setDirty();
  };

  // Handle Drag Top (Red Line)
  const dragOffsetTop = useRef({ x: 0, y: 0 });

  const onHandleDragTop = (event: PointerEvent) => {
    const [, worldY] = projectToWorld(event.clientX, event.clientY, gl, camera);
    const dy = worldY - square.position.y - dragOffsetTop.current.y;
    floorplannerStore.updateSymbolProperty(square.id, "squareHeight", Math.max(0.1, squareHeight + dy * flipY));
  };

  const onPointerUpHandleTop = () => {
    gl.domElement.removeEventListener("pointermove", onHandleDragTop);
    gl.domElement.removeEventListener("pointerup", onPointerUpHandleTop);
    floorplannerStore.setBlockDirty(false);
    floorplannerStore.updateSymbolProperty(square.id, "squareHeight", squareHeight);
    floorplannerStore.setDirty();
  };

  useEffect(() => {
    editorStore.updateGroupRef(square.id, groupRef.current);
  }, [groupRef.current]);

  // Redraw when the symbol.selected changes
  useEffect(() => {
  }, [square.selected]);

  useEffect(() => {
    if (isResizing) return;
    setSquareWidth(Number.isFinite(square.squareWidth) ? square.squareWidth : 1);
    setSquareHeight(Number.isFinite(square.squareHeight) ? square.squareHeight : 1);
    setLocalPosition([square.position.x, square.position.y]);
  }, [isResizing, square.squareWidth, square.squareHeight, square.position.x, square.position.y]);

  return (
    <group
      position={[0, 0, (square.zIndex * zIndexEpsilon)]}
    >
      <ShapeProjectionOverlay symbol={square} />
      <DraggableObject
        position={localPosition}
        onDragStart={(offset) => {
          setIsDragging(true);
          onDragStart(square, offset)
        }}
        onDragEnd={(endPosition) => {
          onDragEnd(endPosition)
          setIsDragging(false);
        }}
        onDrag={handleDrag}
        selectable={true}
        attachmentId={square.id}
        attachmentType="noAttachments"
        symbol={square}
      >
        <SelectableSymbol
          handleSize={floorplannerStore.symbolHandleSize}
          calculateBoundingBox={calculateBoundingBox}
          onResizeStart={() => setIsResizing(true)}
          onResize={(newWidth, newHeight, _boundingBox, _handle, newPosition) => {
            setSquareWidth(newWidth);
            setSquareHeight(newHeight);
            if (newPosition) {
              setLocalPosition(newPosition);
            }
          }}
          onResizeEnd={(_event, finalResize) => {
            setIsResizing(false);
            if (!finalResize) return;
            floorplannerStore.updateSymbolProperty(square.id, "squareWidth", finalResize.width);
            floorplannerStore.updateSymbolProperty(square.id, "squareHeight", finalResize.height);
            if (finalResize.position) {
              floorplannerStore.updateSymbolProperty(square.id, "position", finalResize.position);
            }
          }}
          center={centerPosition}
          rotation={square.rotation}
          symbol={square}
          drawHandles={true}
          pivot={pivot}
          onPointerOver={() => setIsHovered(true)}
          onPointerOut={() => setIsHovered(false)}
          isDragging={isDragging}
          lockAspectRatio={isSymbolAspectRatioLocked(square.lockAspectRatio)}
        >
          <group
            key={`square-opacity-${Math.round(fillOpacity * 100)}-${getOpacityRemountEpoch(square.id)}`}
            ref={groupRef}
            rotation={[0, 0, square.rotation || 0]}
            onPointerEnter={() => {
              if (document.body.style.cursor !== "grabbing" && document.body.style.cursor !== "move") document.body.classList.add("cursor-vector");
            }}
            onPointerLeave={() => {
              if (document.body.style.cursor !== "grabbing" && document.body.style.cursor !== "move") document.body.classList.remove("cursor-vector");
            }}
          >
            {Number.isFinite(squareWidth) && Number.isFinite(squareHeight) && squareWidth > 0 && squareHeight > 0 && (
              <mesh>
                <planeGeometry args={[squareWidth, squareHeight]} />
                <PatternFillMesh
                  fillColor={square.fillColor || 0xffffff}
                  fillPattern={square.fillPattern}
                  useWorldSpace={false}
                  useLocalPositionCoords={true}
                  side={THREE.DoubleSide}
                  opacity={projectionActive ? Math.min(fillOpacity, 0.08) : fillOpacity}
                  transparent={projectionActive || fillIsTransparent}
                  depthWrite={!projectionActive && !fillIsTransparent}
                />
              </mesh>
            )}
            {/* Render top horizontal line */}
            <Line
              key={`square-top-${Math.round((projectionActive ? Math.min(fillOpacity, 0.08) : fillOpacity) * 100)}`}
              points={validatePoints([
                [-squareWidth / 2 * flipX, squareHeight / 2, 0],
                [squareWidth / 2, squareHeight / 2, 0]
              ])}
              color={projectionActive ? 0x94a3b8 : canvasAccentColor(square.selected, !!isHovered, lineColor)}
              lineWidth={projectionActive ? 0.8 : lineWeight}
              opacity={projectionActive ? Math.min(fillOpacity, 0.08) : fillOpacity}
              transparent={projectionActive || fillIsTransparent}
              depthWrite={!projectionActive && !fillIsTransparent}
              {...((projectionActive || square.lineType === "dashed") && {
                dashed: true,
                dashSize: 0.15,
                gapSize: 0.1,
              })}
              raycast={noopRaycast}
            />

            {/* Render bottom horizontal line */}
            <Line
              key={`square-bottom-${Math.round((projectionActive ? Math.min(fillOpacity, 0.08) : fillOpacity) * 100)}`}
              points={validatePoints([
                [-squareWidth / 2, -squareHeight / 2, 0],
                [squareWidth / 2, -squareHeight / 2, 0]
              ])}
              color={projectionActive ? 0x94a3b8 : canvasAccentColor(square.selected, !!isHovered, lineColor)}
              lineWidth={projectionActive ? 0.8 : lineWeight}
              opacity={projectionActive ? Math.min(fillOpacity, 0.08) : fillOpacity}
              transparent={projectionActive || fillIsTransparent}
              depthWrite={!projectionActive && !fillIsTransparent}
              {...((projectionActive || square.lineType === "dashed") && {
                dashed: true,
                dashSize: 0.15,
                gapSize: 0.1,
              })}
              raycast={noopRaycast}
            />

            {/* Render left vertical line */}
            <Line
              key={`square-left-${Math.round((projectionActive ? Math.min(fillOpacity, 0.08) : fillOpacity) * 100)}`}
              points={validatePoints([
                [-squareWidth / 2, squareHeight / 2, 0],
                [-squareWidth / 2, -squareHeight / 2, 0]
              ])}
              color={projectionActive ? 0x94a3b8 : canvasAccentColor(square.selected, !!isHovered, lineColor)}
              lineWidth={projectionActive ? 0.8 : lineWeight}
              opacity={projectionActive ? Math.min(fillOpacity, 0.08) : fillOpacity}
              transparent={projectionActive || fillIsTransparent}
              depthWrite={!projectionActive && !fillIsTransparent}
              {...((projectionActive || square.lineType === "dashed") && {
                dashed: true,
                dashSize: 0.15,
                gapSize: 0.1,
              })}
              raycast={noopRaycast}
            />

            {/* Render right vertical line */}
            <Line
              key={`square-right-${Math.round((projectionActive ? Math.min(fillOpacity, 0.08) : fillOpacity) * 100)}`}
              points={validatePoints([
                [squareWidth / 2, squareHeight / 2, 0],
                [squareWidth / 2, -squareHeight / 2, 0]
              ])}
              color={projectionActive ? 0x94a3b8 : canvasAccentColor(square.selected, !!isHovered, lineColor)}
              lineWidth={projectionActive ? 0.8 : lineWeight}
              opacity={projectionActive ? Math.min(fillOpacity, 0.08) : fillOpacity}
              transparent={projectionActive || fillIsTransparent}
              depthWrite={!projectionActive && !fillIsTransparent}
              {...((projectionActive || square.lineType === "dashed") && {
                dashed: true,
                dashSize: 0.15,
                gapSize: 0.1,
              })}
              raycast={noopRaycast}
            />
          </group>
        </SelectableSymbol>
      </DraggableObject>
    </group>
  );
});

export default SquareSymbol;
